import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import { designTokens } from "@itandi/itandi-bb-ui";
import { Alert } from "@itandi/itandi-bb-ui/Alert";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { Table } from "@itandi/itandi-bb-ui/Table";
import { Tbody } from "@itandi/itandi-bb-ui/Tbody";
import { Td } from "@itandi/itandi-bb-ui/Td";
import { Th } from "@itandi/itandi-bb-ui/Th";
import { Thead } from "@itandi/itandi-bb-ui/Thead";
import { Toggle } from "@itandi/itandi-bb-ui/Toggle";
import { Tr } from "@itandi/itandi-bb-ui/Tr";
import { useCallback, useState } from "react";
import { useSWRConfig } from "swr";
import useSWRMutation from "swr/mutation";

import type { VendorPartner } from "~/models/VendorPartner";
import { isSuccess } from "~/utils/api/api_handler";
import { destroy, update } from "~/utils/api/vendors/VendorPartner";
import { generatePath } from "~/utils/generatePath";

type Props = DeepReadonly<{
	vendorPartners: VendorPartner[];
}>;

export function VendorPartnerList({ vendorPartners }: Props) {
	const { notify } = useNotification();
	const [deleteTarget, setDeleteTarget] = useState<VendorPartner | null>(null);
	const { mutate: globalMutate } = useSWRConfig();

	const { trigger: updateTrigger, isMutating: isUpdating } = useSWRMutation(
		generatePath("/vendors/vendor_partners"),
		async (
			_key,
			{ arg }: { arg: { newValue: boolean; partnerId: number } }
		) => {
			return await update(arg.partnerId, arg.newValue);
		}
	);

	const handleUpdateAllowMessage = useCallback(
		async (partner: VendorPartner, newValue: boolean) => {
			const result = await updateTrigger(
				{ newValue, partnerId: partner.id },
				{
					optimisticData: (currentData: VendorPartner[] | undefined) => {
						if (!currentData) return [];
						const index = currentData.findIndex((p) => p.id === partner.id);
						if (index === -1) return currentData;
						return currentData.with(index, {
							...currentData[index],
							allowMessage: newValue
						});
					},
					revalidate: true,
					rollbackOnError: true
				}
			);
			if (isSuccess(result)) {
				notify({ content: "チャット許可を更新しました。", variant: "success" });
			} else {
				notify({ content: "エラーが発生しました。", variant: "error" });
			}
		},
		[updateTrigger, notify]
	);

	const { trigger: destroyTrigger, isMutating: isDeleting } = useSWRMutation(
		generatePath("/vendors/vendor_partners"),
		async (_key, { arg }: { arg: { partnerId: number } }) => {
			return await destroy(arg.partnerId);
		}
	);

	const handleDestroySubmit = useCallback(async () => {
		const result = await destroyTrigger({ partnerId: deleteTarget!.id });

		if (isSuccess(result)) {
			notify({ content: "チャット許可を更新しました。", variant: "success" });
			await globalMutate(generatePath("/vendors/vendor_partners"));
			setDeleteTarget(null);
		} else {
			notify({ content: "エラーが発生しました。", variant: "error" });
		}
	}, [deleteTarget, destroyTrigger, globalMutate, notify]);

	return (
		<>
			<Table>
				<Thead>
					<Tr>
						<Th>協力会社名</Th>
						<Th width="7rem">チャット許可</Th>
						<Th width="5rem">操作</Th>
					</Tr>
				</Thead>
				<Tbody>
					{vendorPartners.map((partner) => (
						<Tr key={partner.id}>
							<Td>{partner.partnerVendorCompany.name}</Td>
							<Td>
								<Toggle
									checked={partner.allowMessage}
									disabled={isUpdating}
									onChange={async () => {
										await handleUpdateAllowMessage(
											partner,
											!partner.allowMessage
										);
									}}
								/>
							</Td>
							<Td>
								<Button
									height="small"
									onClick={() => {
										setDeleteTarget(partner);
									}}
									variant="danger"
								>
									削除
								</Button>
							</Td>
						</Tr>
					))}
				</Tbody>
			</Table>
			{deleteTarget ? (
				<Modalv2
					isOpen
					onClose={() => {
						setDeleteTarget(null);
					}}
					size="s"
				>
					<ModalHeader title="協力会社の削除" />
					<ModalBody>
						「{deleteTarget.partnerVendorCompany.name}
						」を協力会社リストから削除します。
						<Alert mt={designTokens["spacing/m"]} variant="warning">
							この操作は元に戻せません。
						</Alert>
					</ModalBody>
					<ModalFooter
						leftArea={
							<Button
								disabled={isDeleting}
								onClick={() => {
									setDeleteTarget(null);
								}}
								variant="secondary"
							>
								キャンセル
							</Button>
						}
						rightArea={
							<Button
								loading={isDeleting}
								onClick={handleDestroySubmit}
								variant="danger"
							>
								削除
							</Button>
						}
					/>
				</Modalv2>
			) : null}
		</>
	);
}
